/**
 * 5897 — Peças compartilhadas pelas telas de Previsões: formatação, KPI,
 * "Entenda melhor", seletor segmentado, estados de carga e a data de
 * referência (?ref=AAAA-MM-DD), que acompanha a navegação entre as telas.
 */
import { FC, ReactElement, ReactNode } from 'react'
import { useSearchParams } from 'react-router-dom'
import styled from 'styled-components'
import { usePrevisoes } from '../../../hooks/previsoes'
import { EstudoPrevisoes, PainelPrevisoes, PrevisaoEvento } from '../../../types/previsoes'
import { BD, Card, KpiCard, PageSubtitle, Select, Spinner, StatusBadge } from '../styles'

/* ── formatação ─────────────────────────────────────────────── */
export const fmtInt = (n: number): string => Math.round(Number(n) || 0).toLocaleString('pt-BR')
export const brl = (n: number): string => {
  const v = Number(n) || 0
  if (Math.abs(v) >= 1e6) return `R$ ${(v / 1e6).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} mi`
  return `R$ ${fmtInt(v)}`
}
export const pct = (v: number): string => `${Math.round((Number(v) || 0) * 100)}%`
/** 'AAAA-MM-DD' → 'DD/MM/AAAA' */
export const fmtData = (iso: string): string => iso.split('-').reverse().join('/')

/* ── cores dos gráficos (paleta do Borderô) ─────────────────── */
export const COR = {
  serie: '#2A3B4C', // Support Blue — dados
  projecao: '#B0020B', // BD Digital Red — destaque
  anterior: '#98A2B3',
  banda: 'rgba(42, 59, 76, 0.12)',
  grade: '#E5E7EB',
  eixo: '#6B7280',
}

export const TOM = {
  ruim: { bg: '#FEF3F2', cor: '#B42318' },
  bom: { bg: '#ECFDF3', cor: '#067647' },
  atencao: { bg: '#FFFAEB', cor: '#B54708' },
  info: { bg: '#EFF8FF', cor: '#175CD3' },
  neutro: { bg: BD.grayBg, cor: BD.grayText },
}

/* ── blocos ─────────────────────────────────────────────────── */
export const Kpis = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 12px;
  margin-bottom: 16px;
`

export const Kpi: FC<{ rotulo: string; valor: ReactNode; dica?: ReactNode }> = ({ rotulo, valor, dica }) => (
  <KpiCard>
    <div className="kpi-label">{rotulo}</div>
    <div className="kpi-value" style={{ fontSize: 24 }}>
      {valor}
    </div>
    {dica !== undefined && <div style={{ fontSize: 12.5, color: BD.grayText, marginTop: 2 }}>{dica}</div>}
  </KpiCard>
)

export const Nota = styled.div<{ $tom?: 'info' | 'atencao' }>`
  background: ${({ $tom }) => TOM[$tom ?? 'info'].bg};
  color: ${({ $tom }) => TOM[$tom ?? 'info'].cor};
  border-radius: 10px;
  padding: 10px 14px;
  font-size: 13px;
  margin-bottom: 16px;
`

const Detalhes = styled.details`
  margin-top: 12px;
  border-top: 1px solid ${BD.grayBorder};
  padding-top: 8px;
  font-size: 13px;
  color: ${BD.grayText};
  summary {
    cursor: pointer;
    color: ${TOM.info.cor};
    font-weight: 500;
  }
  p {
    margin: 8px 0 0;
    max-width: 90ch;
  }
`

/** O "Entenda melhor" de cada gráfico e tabela: o que se lê ali e os cuidados. */
export const Entenda: FC<{ paragrafos: string[] }> = ({ paragrafos }) => (
  <Detalhes>
    <summary>Entenda melhor</summary>
    {paragrafos.map((p) => (
      <p key={p}>{p}</p>
    ))}
  </Detalhes>
)

export const CartaoTabela = styled(Card)`
  padding: 0;
  overflow: hidden;
  .titulo {
    padding: 16px 16px 8px;
  }
  .titulo h3 {
    margin: 0;
    font-size: 15px;
    font-weight: 700;
  }
`

export const Rolagem = styled.div`
  overflow-x: auto;
  td.n,
  th.n {
    text-align: right;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
  }
  td small,
  .faixa {
    display: block;
    font-size: 12px;
    color: ${BD.grayText};
    font-weight: 400;
  }
`

export const Segmentos = styled.div`
  display: inline-flex;
  flex-wrap: wrap;
  border: 1px solid ${BD.grayBorder};
  border-radius: 8px;
  overflow: hidden;
  background: #fff;
  button {
    font: inherit;
    font-size: 13px;
    padding: 7px 12px;
    border: 0;
    background: #fff;
    color: ${BD.grayText};
    cursor: pointer;
  }
  button[aria-pressed='true'] {
    background: ${BD.black};
    color: #fff;
  }
`

export function Segmentado<T extends string>({
  rotulo,
  opcoes,
  valor,
  onChange,
}: {
  rotulo: string
  opcoes: Array<[T, string]>
  valor: T
  onChange: (v: T) => void
}): ReactElement {
  return (
    <Segmentos role="group" aria-label={rotulo}>
      {opcoes.map(([k, l]) => (
        <button key={k} type="button" aria-pressed={k === valor} onClick={() => onChange(k)}>
          {l}
        </button>
      ))}
    </Segmentos>
  )
}

export const Selo: FC<{ tom: keyof typeof TOM; children: ReactNode }> = ({ tom, children }) => (
  <StatusBadge $bg={TOM[tom].bg} $color={TOM[tom].cor} style={{ margin: '0 4px 4px 0', whiteSpace: 'nowrap' }}>
    {children}
  </StatusBadge>
)

export const SeloAlerta: FC<{ alerta: string }> = ({ alerta }) => {
  const tom = alerta.includes('caiu') || alerta.includes('sem vendas') ? 'ruim' : alerta.includes('subiu') ? 'bom' : 'atencao'
  return <Selo tom={tom}>{alerta.replace(' (pode indicar)', '')}</Selo>
}

export const SeloConfianca: FC<{ evento: PrevisaoEvento }> = ({ evento }) =>
  evento.confianca === 'baixa' ? (
    <Selo tom="atencao">confiança baixa</Selo>
  ) : evento.confianca === 'média' ? (
    <Selo tom="atencao">faixa larga</Selo>
  ) : (
    <Selo tom="bom">boa</Selo>
  )

/* ── data de referência ─────────────────────────────────────── */
/** A data escolhida (?ref=) ou undefined para o snapshot mais recente. */
export function useReferencia(): [string | undefined, (r: string | undefined) => void] {
  const [params, setParams] = useSearchParams()
  const ref = params.get('ref') ?? undefined
  const mudar = (r: string | undefined): void => {
    const p = new URLSearchParams(params)
    if (r) p.set('ref', r)
    else p.delete('ref')
    setParams(p)
  }
  return [ref, mudar]
}

/** Sufixo de URL para manter a data escolhida ao navegar entre as telas. */
export const comRef = (caminho: string, ref?: string): string => (ref ? `${caminho}?ref=${ref}` : caminho)

const Topo = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 16px;
  .info {
    font-size: 12.5px;
    color: ${BD.grayText};
  }
`

/** Seletor da data do snapshot + "dados até" e horário do cálculo. */
export const BarraReferencia: FC<{ painel: PainelPrevisoes; referencias: string[] }> = ({ painel, referencias }) => {
  const [ref, mudar] = useReferencia()
  const gerado = new Date(`${painel.gerado_em}Z`)
  return (
    <Topo>
      <div className="info">
        Vendas até {fmtData(painel.dados_ate)} · calculado em {gerado.toLocaleString('pt-BR')}
      </div>
      {referencias.length > 1 && (
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: BD.grayText }}>
          Previsão de
          <Select
            style={{ width: 'auto' }}
            value={ref ?? referencias[0]}
            onChange={(e) => mudar(e.target.value === referencias[0] ? undefined : e.target.value)}
          >
            {referencias.map((r) => (
              <option key={r} value={r}>
                {fmtData(r)}
              </option>
            ))}
          </Select>
        </label>
      )}
    </Topo>
  )
}

const Centro = styled.div`
  padding: 64px 12px;
  text-align: center;
  color: ${BD.grayText};
`

const Carregando: FC = () => (
  <Centro>
    <Spinner />
    <p style={{ marginTop: 12 }}>Carregando previsões…</p>
  </Centro>
)
const Erro: FC = () => (
  <Centro>
    <PageSubtitle>Não foi possível carregar as previsões. Tente de novo em instantes.</PageSubtitle>
  </Centro>
)

/**
 * Carrega o painel e trata carregando / erro / sem snapshot; só chama `children`
 * quando há dados. O estudo vem junto na mesma resposta.
 */
export const ComPainel: FC<{ children: (p: PainelPrevisoes, referencias: string[], estudo: EstudoPrevisoes) => ReactNode }> = ({
  children,
}) => {
  const [ref] = useReferencia()
  const { painel, referencias, estudo, vazio, isLoading, isError } = usePrevisoes(ref)
  if (isLoading) return <Carregando />
  if (isError || (painel && !estudo)) return <Erro />
  if (vazio || !painel || !estudo)
    return (
      <Centro>
        <PageSubtitle>
          Ainda não há previsão calculada{ref ? ` para ${fmtData(ref)}` : ''}. O cálculo roda todo dia de madrugada.
        </PageSubtitle>
      </Centro>
    )
  return <>{children(painel, referencias, estudo)}</>
}

/**
 * Só os números fixos do estudo (servidos pelo analytics, atrás do mesmo gate
 * das previsões, para não irem no bundle público); não depende de haver snapshot.
 */
export const ComEstudo: FC<{ children: (estudo: EstudoPrevisoes) => ReactNode }> = ({ children }) => {
  const { estudo, isLoading, isError } = usePrevisoes()
  if (isLoading) return <Carregando />
  if (isError || !estudo) return <Erro />
  return <>{children(estudo)}</>
}
